import { Box, Card, Container, Stack } from "@mui/material";
import React, { useState } from "react";
// import logo from "../../assets/logo.png";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomisation } from "../../context/CustomisationContext";
import { useToast } from "../../context/ToastContext";
import { useAppBootstrap } from "../../hooks/useAppBootstrap";
import { useTranslate } from "../../hooks/useTranslate";
import {
  confirmForgotPassword,
  createUser,
  firstTimeLogin,
  getUserPool,
  login,
} from "../../services/api/apiClient";
import { setAxiosUserEmail } from "../../services/api/axiosInstance";
import { confirmForgotPasswordParams } from "../../services/api/types/auth/confirmForgotPassword";
import { firstTimeLoginParams } from "../../services/api/types/auth/firstTimeLogin";
import { loginUserParams } from "../../services/api/types/auth/login";
import {
  LoginFlow,
  UserEmailQueryParams,
} from "../../services/api/types/auth/userPool";
import { CreateUserBody } from "../../services/api/types/general/user";
import { UserStatusType } from "../../services/api/types/users/users";
import { FooterLogo } from "../components/FooterLogo";
import { NewPasswordForm } from "./components/NewPasswordForm";
import { PasswordReset } from "./components/PasswordReset";

interface NewPasswordProps {}

export const NewPassword: React.FC<NewPasswordProps> = () => {
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordReset, setPasswordReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { customisations } = useCustomisation();
  const runBootstrap = useAppBootstrap();
  const t = useTranslate("new-password");
  const footerText = useTranslate("login")("footer","poweredBy")
  const {
    LOGIN: {
      backgroundSettings: loginBgSettings,
      logoSettings: loginLogoSettings,
    },
    GPC: { backgroundSettings: gpcBgSettings, logoSettings: gpcLogoSettings },
  } = customisations.customisations;
  const logo =
    loginLogoSettings?.loginLandscapeLogo ?? gpcLogoSettings?.gpcLandscapeLogo;
  const primaryBgColor =
    loginBgSettings?.loginPrimaryBgColor ?? gpcBgSettings?.gpcPrimaryBgColor;
  const secondaryBgColor =
    loginBgSettings?.loginSecondaryBgColor ??
    gpcBgSettings?.gpcSecondaryBgColor;
  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasSpecialChar = /[!@#$%*]/.test(password);

  const stateParams =
    (location.state as {
      userName?: string;
      confirmationCode?: string;
      form?: string;
      expiration?: number;
    }) ?? {};

  const userName = searchParams.get("userName") ?? stateParams.userName;
  const confirmationCode =
    searchParams.get("confirmationCode") ?? stateParams.confirmationCode;
  const form = searchParams.get("form") ?? stateParams.form;
  const expirationParam = searchParams.get("expiration");
  const expiration = expirationParam
    ? Number(expirationParam)
    : stateParams.expiration;

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordError("");
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmPasswordError("");
    setConfirmPassword(event.target.value);
  };

  const resetPassword = async () => {
    try {
      if (!hasMinLength || !hasLowercase || !hasUppercase || !hasSpecialChar) {
        setPasswordError(t("setNewPasswordMessages", "passwordRequirementsNotMet"));
        return;
      }
      if (password !== confirmPassword) {
        setConfirmPasswordError(t("setNewPasswordMessages", "passwordsDontMatch"));
        return;
      }
      setLoading(true);
      if (form === "ca") {
        if (userName && confirmationCode && expiration) {
          const emailQueryParams: UserEmailQueryParams = {
            email: userName,
            loginFlow: LoginFlow.FIRST_TIME_LOGIN,
            portalUrl: window.location.origin,
          };
          const userPoolResponse = await getUserPool(emailQueryParams);
          setAxiosUserEmail(userPoolResponse.cognitoUser);

          const body: firstTimeLoginParams = {
            username: userName,
            temporaryPassword: confirmationCode,
            inFlow: true,
            newPassword: password,
            poolId: userPoolResponse.poolId,
            clientId: userPoolResponse.clientId,
            portalId: userPoolResponse.portalId,
            userId: userPoolResponse.userId,
            expiration: expiration,
          };
          const response: { cognitoUser: string } = await firstTimeLogin(body);

          const loginBody: loginUserParams = {
            username: userName,
            password,
            inFlow: false,
            poolId: userPoolResponse.poolId,
            clientId: userPoolResponse.clientId,
            portalId: userPoolResponse.portalId,
            userId: userPoolResponse.userId,
          };
          await login(loginBody);

          localStorage.setItem("userName", userName);
          localStorage.setItem("rememberMe", JSON.stringify(true));

          const createBody: CreateUserBody = {
            status: UserStatusType.ACTIVE,
            email: userName,
            cognitoUser: response.cognitoUser,
            portalUrl: window.location.origin,
          };
          await createUser(createBody);

          await runBootstrap();
          navigate("/home");
        }
      } else {
        if (userName && confirmationCode) {
          const emailQueryParams: UserEmailQueryParams = {
            email: userName,
            loginFlow: LoginFlow.LOGIN,
            portalUrl: window.location.origin,
          };
          const userPoolResponse = await getUserPool(emailQueryParams);

          const body: confirmForgotPasswordParams = {
            username: userName,
            confirmationCode: confirmationCode,
            newPassword: password,
            clientId: userPoolResponse.clientId,
          };
          await confirmForgotPassword(body);
          setPasswordReset(true);
        }
      }
    } catch (error: any) {
      console.error("Error", error);
      showToast(error.response.data, "error");
      const message =
        typeof error.response?.data === "string" ? error.response.data : "";
      const isExpired =
        error.response?.status === 410 || /expired/i.test(message);
      if (isExpired) {
        setTimeout(() => {
          navigate("/link-expired", {
            state: { userName, reason: "temp-password-expired" },
            replace: true,
          });
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        py: 4,
        bgcolor: primaryBgColor,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Container maxWidth="lg">
          <Card
            sx={{
              maxWidth: 440,
              mx: "auto",
              p: 4,
              borderRadius: 2,
              boxShadow:
                "0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)",
              bgcolor: secondaryBgColor,
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 50,
                  maxWidth: 200,
                }}
              >
                <Box
                  component="img"
                  src={`/${logo}`}
                  alt="logo"
                  sx={{
                    height: 50,
                    maxWidth: 200,
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
              {!passwordReset ? (
                <NewPasswordForm
                  password={password}
                  confirmPassword={confirmPassword}
                  handlePasswordChange={handlePasswordChange}
                  handleConfirmPasswordChange={handleConfirmPasswordChange}
                  confirmPasswordError={confirmPasswordError}
                  passwordError={passwordError}
                  resetPassword={resetPassword}
                  loading={loading}
                  activateAccount={form === "ca"}
                  passwordValidation={{
                    hasMinLength,
                    hasLowercase,
                    hasUppercase,
                    hasSpecialChar,
                  }}
                />
              ) : (
                <PasswordReset />
              )}
            </Stack>
          </Card>
        </Container>
      </Box>
      {customisations.customisations.LOGIN.logoSettings?.loginPoweredBy?.toLowerCase() !==
      "true" ? (
        <FooterLogo poweredByText={footerText} />
      ) : null}
    </Box>
  );
};
